
import React, { useState, useEffect, useCallback } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from '@/integrations/supabase/client';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { TableLoadingOverlay } from "@/components/ui/table-loading-overlay";
import { format, parseISO } from 'date-fns';
import { ExternalLink, Copy, Check, RefreshCw, AlertCircle, Search } from 'lucide-react';
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { TwitterSearchResult } from '@/types/twitterTypes';
import { useTwitterJobStatus } from '@/hooks/use-twitter-job-status';

interface TwitterResultsTableProps {
  deliverableId: string;
}

interface EnhancedTwitterSearchResult extends TwitterSearchResult {
  deliverable_name?: string;
}

export const TwitterResultsTable: React.FC<TwitterResultsTableProps> = ({ deliverableId }) => {
  const [tweets, setTweets] = useState<EnhancedTwitterSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sortColumn, setSortColumn] = useState<string>('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAuthor, setSelectedAuthor] = useState<string | null>(null);
  const [uniqueAuthors, setUniqueAuthors] = useState<{id: string; username: string; name: string}[]>([]);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [deliverableInfo, setDeliverableInfo] = useState<{id: string, name: string}[]>([]);
  const [showDeliverables, setShowDeliverables] = useState<boolean>(false);

  const { 
    jobStatus, 
    processingProgress, 
    errorMessage,
    startBackgroundProcessing,
    cancelProcessing
  } = useTwitterJobStatus(deliverableId);

  // Define columns here, before they are used
  const columns = [
    {
      id: 'author',
      header: 'Author',
      cell: ({ row }: { row: any }) => {
        const tweet = row.original;
        return (
          <div className="flex items-center gap-2">
            <img 
              src={tweet.profile_image_url || 'https://abs.twimg.com/sticky/default_profile_images/default_profile.png'} 
              alt={tweet.author_name || tweet.author_username || 'Unknown'} 
              className="w-8 h-8 rounded-full"
            />
            <div>
              <div className="font-medium">{tweet.author_name || 'Unknown'}</div>
              <div className="text-xs text-muted-foreground">@{tweet.author_username || 'unknown'}</div>
            </div>
          </div>
        );
      }
    },
    {
      id: 'tweet',
      header: 'Tweet',
      cell: ({ row }: { row: any }) => {
        const tweet = row.original;
        return (
          <Dialog>
            <DialogTrigger asChild>
              <div className="cursor-pointer line-clamp-2 hover:underline">
                {tweet.text}
              </div>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Avatar className="h-6 w-6">
                    <AvatarImage 
                      src={tweet.profile_image_url} 
                      alt={tweet.author_name || ''} 
                    />
                    <AvatarFallback>
                      {tweet.author_name?.charAt(0) || '?'}
                    </AvatarFallback>
                  </Avatar>
                  <span>{tweet.author_name}</span>
                  <span className="text-muted-foreground text-sm font-normal">@{tweet.author_username}</span>
                </DialogTitle>
                <DialogDescription className="text-right text-xs">
                  {tweet.created_at && format(parseISO(tweet.created_at), "MMM d, yyyy 'at' h:mm a")}
                </DialogDescription>
              </DialogHeader>
              <div className="whitespace-pre-wrap">{tweet.text}</div>
              <div className="flex gap-4 text-sm text-muted-foreground">
                <div>❤️ {tweet.like_count || 0}</div>
                <div>🔄 {tweet.retweet_count || 0}</div>
                <div>💬 {tweet.reply_count || 0}</div>
              </div>
              
              {tweet.deliverable_name && (
                <div className="mt-3">
                  <Badge variant="primary">{tweet.deliverable_name}</Badge>
                </div>
              )}
            </DialogContent>
          </Dialog>
        );
      }
    },
    {
      id: 'deliverable',
      header: 'Deliverable',
      cell: ({ row }: { row: any }) => {
        const tweet = row.original;
        if (!showDeliverables) return null;
        return (
          tweet.deliverable_name ? (
            <Badge variant="primary" className="whitespace-nowrap">
              {tweet.deliverable_name}
            </Badge>
          ) : null
        );
      }
    },
    {
      id: 'date',
      header: 'Date',
      cell: ({ row }: { row: any }) => {
        const tweet = row.original;
        return (
          <div className="whitespace-nowrap">
            {tweet.created_at && format(parseISO(tweet.created_at), "MMM d, yyyy")}
          </div>
        );
      }
    },
    {
      id: 'likes',
      header: 'Likes',
      cell: ({ row }: { row: any }) => {
        const tweet = row.original;
        return (
          <div className="text-right">
            {tweet.like_count || 0}
          </div>
        );
      }
    },
    {
      id: 'retweets',
      header: 'Retweets',
      cell: ({ row }: { row: any }) => {
        const tweet = row.original;
        return (
          <div className="text-right">
            {tweet.retweet_count || 0}
          </div>
        );
      }
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }: { row: any }) => {
        const tweet = row.original;
        return (
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => copyToClipboard(tweet.tweet_id)}
              title="Copy Tweet ID"
            >
              {copiedId === tweet.tweet_id ? (
                <Check className="h-4 w-4 text-green-500" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => openInTwitter(tweet.tweet_id)}
              title="Open in Twitter"
            >
              <ExternalLink className="h-4 w-4" />
            </Button>
          </div>
        );
      }
    }
  ];

  // Function to get all deliverables (for multiple deliverable view)
  const fetchDeliverableInfo = async () => {
    try {
      const { data, error } = await supabase
        .from('twitter_deliverables')
        .select('id, name');
      
      if (error) throw error;
      
      if (data) {
        setDeliverableInfo(data);
      }
    } catch (error) {
      console.error('Error fetching deliverable info:', error);
    }
  };

  // Function to get all tweets for the deliverable
  const fetchTweets = useCallback(async () => {
    setIsLoading(true);
    try {
      // If deliverableId is provided, fetch tweets for that specific deliverable
      let query = supabase.from('twitter_search_results').select('*');
      
      if (deliverableId) {
        query = query.eq('deliverable_id', deliverableId);
        setShowDeliverables(false);
      } else {
        // If no deliverableId is provided, show all tweets and include deliverable info
        setShowDeliverables(true);
        await fetchDeliverableInfo();
      }
      
      query = query.order(sortColumn, { ascending: sortDirection === 'asc' });
      
      const { data, error } = await query;

      if (error) throw error;

      if (data) {
        console.log(`Fetched ${data.length} tweets${deliverableId ? ` for deliverable ${deliverableId}` : ''}`);
        
        // Enhance tweets with deliverable name if needed
        if (showDeliverables && deliverableInfo.length > 0) {
          const enhancedTweets = data.map(tweet => {
            const matchingDeliverable = deliverableInfo.find(d => d.id === tweet.deliverable_id);
            return {
              ...tweet,
              deliverable_name: matchingDeliverable ? matchingDeliverable.name : 'Unknown'
            };
          });
          setTweets(enhancedTweets as EnhancedTwitterSearchResult[]);
        } else {
          setTweets(data as EnhancedTwitterSearchResult[]);
        }
        
        // Extract unique authors
        const authors = Array.from(new Map(
          data.map(tweet => [
            tweet.author_id, 
            { 
              id: tweet.author_id, 
              username: tweet.author_username, 
              name: tweet.author_name 
            }
          ])
        ).values());
        
        setUniqueAuthors(authors);
      } else {
        setTweets([]);
      }
    } catch (error) {
      console.error('Error fetching tweets:', error);
    } finally {
      setIsLoading(false);
    }
  }, [deliverableId, sortColumn, sortDirection, deliverableInfo, showDeliverables]);

  // Initial load and when sort changes
  useEffect(() => {
    fetchTweets();
  }, [fetchTweets]);
  
  // Refresh when job status becomes completed
  useEffect(() => {
    if (jobStatus === 'completed') {
      fetchTweets();
    }
  }, [jobStatus, fetchTweets]);

  // Function to change sort
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc'); // Default to desc when changing columns
    }
  };
  
  // Copy tweet ID to clipboard
  const copyToClipboard = (id: string) => {
    navigator.clipboard.writeText(id);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };
  
  // Open tweet in Twitter
  const openInTwitter = (tweetId: string) => {
    window.open(`https://twitter.com/i/web/status/${tweetId}`, '_blank');
  };

  // Filter tweets based on search and author selection
  const filteredTweets = tweets.filter(tweet => {
    const matchesSearch = !searchTerm || 
      (tweet.text?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false) ||
      (tweet.author_username?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false) ||
      (tweet.author_name?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false);
      
    const matchesAuthor = !selectedAuthor || tweet.author_id === selectedAuthor;
    
    return matchesSearch && matchesAuthor;
  });

  // Filter out the deliverable column if we're viewing a specific deliverable
  const visibleColumns = columns.filter(col => 
    col.id !== 'deliverable' || showDeliverables
  );

  return (
    <div className="relative space-y-4">
      {isLoading && <TableLoadingOverlay isLoading={true} message="Loading tweets..." />}
      
      {jobStatus === 'processing' && (
        <Alert variant="warning" className="mb-4">
          <div className="flex justify-between w-full items-center">
            <div className="flex items-start gap-2">
              <RefreshCw className="h-4 w-4 animate-spin mt-0.5" />
              <div>
                <AlertTitle>Processing in Progress</AlertTitle>
                <AlertDescription>
                  A background job is currently running to fetch tweets.
                  {processingProgress > 0 && (
                    <div className="mt-2">
                      <div className="w-full h-2 bg-secondary rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-primary transition-all duration-500" 
                          style={{ width: `${processingProgress}%` }}
                        ></div>
                      </div>
                      <p className="text-xs text-right mt-1">
                        {processingProgress}% complete
                      </p>
                    </div>
                  )}
                </AlertDescription>
              </div>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={cancelProcessing}
            >
              Cancel
            </Button>
          </div>
        </Alert>
      )}
      
      {jobStatus === 'failed' && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Processing Failed</AlertTitle>
          <AlertDescription>
            {errorMessage || 'The background job to fetch tweets encountered an error. Please try again.'}
          </AlertDescription>
        </Alert>
      )}
      
      <div className="flex flex-col md:flex-row gap-3 justify-between">
        <div className="flex flex-1 gap-2">
          <Input
            placeholder="Search tweets..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="max-w-xs"
          />
          
          <Select
            value={selectedAuthor || ''}
            onValueChange={(value) => setSelectedAuthor(value || null)}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by author" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Authors</SelectItem>
              {uniqueAuthors.map((author) => (
                <SelectItem key={author.id} value={author.id}>
                  @{author.username}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={fetchTweets}
            disabled={isLoading}
          >
            <RefreshCw className={`h-4 w-4 mr-1 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>
      
      {tweets.length === 0 && !isLoading ? (
        <div className="py-12 text-center">
          <p className="text-muted-foreground">
            No tweets found for this deliverable.
            {jobStatus === 'processing' && (
              <span className="block mt-2">A background process is currently retrieving new tweets.</span>
            )}
          </p>
        </div>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                {visibleColumns.map(column => (
                  <TableHead 
                    key={column.id}
                    onClick={() => handleSort(column.id)}
                    className="cursor-pointer"
                  >
                    {column.header}
                    {sortColumn === column.id && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTweets.map((tweet) => (
                <TableRow key={tweet.id}>
                  {visibleColumns.map(column => (
                    <TableCell key={column.id} className="align-top">
                      {column.cell({ row: { original: tweet } })}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};
